import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTopicList } from '../api/liveCommentService';
import { ROUTES } from '@app/router/routes';
import LiveBadge from '@shared/components/LiveBadge';
import { useLanguage } from '@shared/contexts/LanguageContext';

function isBoardActive(board) {
  if (!board || board.settled) return false;
  const now = Date.now();
  return now >= new Date(board.start_time).getTime() && now <= new Date(board.end_time).getTime();
}

function isBoardEnded(board) {
  if (!board || board.settled) return false;
  const endTime = new Date(board.end_time).getTime();
  return Number.isFinite(endTime) && Date.now() > endTime;
}

export default function TopicListPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';
  const [board, setBoard] = useState(null);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    getTopicList(boardId).then(res => {
      if (res.success && res.data) {
        setBoard(res.data.board_details ?? null);
        setTopics(res.data.items ?? []);
      }
    });
  }, [boardId]);

  return (
    <div className="min-h-[calc(100dvh-120px)] bg-[#FAF7F3]">

      {/* Hero */}
      <div className="relative border-b border-[#E8DCC8] text-center -mt-[70px] md:-mt-[86px] overflow-hidden md:min-h-[calc(86px+160px)]">
        <div className="pt-[70px] md:pt-0">
          <img
            src="/image/live-comment/header_mobile.jpg"
            alt=""
            className="block md:hidden w-full"
          />
          <img
            src="/image/live-comment/header_desktop.jpg"
            alt=""
            className="hidden md:block absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-[70px] md:pt-[86px] px-4 pb-6">
          <div className="max-w-lg mx-auto w-full">
            <div className="flex mb-4">
              <button
                onClick={() => navigate(ROUTES.LIVE_COMMENT)}
                className="inline-flex items-center gap-1.5 text-sm text-[--color-text-sub] hover:text-[--color-primary] bg-white/70 hover:bg-white px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer shadow-sm"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back_ios</span>
                {t('live-comment.heroTitle')}
              </button>
            </div>
            <p className="text-sm tracking-[0.3em] text-[--color-accent] mb-1 uppercase">Select Session</p>
            <h1 className={`${isEn ? 'text-base' : 'text-3xl'} font-black text-[--color-text-main]`}>{board?.title ?? '…'}</h1>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-8 h-px bg-[--color-accent]" />
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-accent]" />
              <div className="w-8 h-px bg-[--color-accent]" />
            </div>
          </div>
        </div>
      </div>

      {/* Topic 列表 */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-3">

        {topics.map(topic => {
          const boardActive = isBoardActive(board);
          const boardSettled = board?.settled ?? false;
          return (
            <button
              key={topic.topic_id}
              onClick={() => navigate(ROUTES.LIVE_COMMENT_TOPIC(boardId, topic.topic_id))}
              className="w-full text-left bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
              style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}
            >
              <div className="flex items-start gap-2 px-5 py-4">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-[--color-buddha-beige] flex items-center justify-center group-hover:bg-[--color-buddha-sand] transition-colors duration-200">
                  <svg width="28" height="28" viewBox="-1 -1 36 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.5 1C8.38704 1 1 8.16285 1 16.9992C1 20.0999 1.91052 22.995 3.48521 25.4471C2.69163 27.2416 1.10758 30.8457 1.21516 30.761L7.50926 29.733C10.2829 31.7815 13.7441 33 17.5 33C26.613 33 34 25.8372 34 17.0008C34 8.16436 26.613 1 17.5 1Z" fill="white" stroke="black" strokeWidth="2" strokeMiterlimit="10"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xl font-medium text-black group-hover:text-[--color-primary] transition-colors duration-200">
                      {topic.title}
                    </span>
                    {boardSettled && (
                      <LiveBadge type="settled" />
                    )}
                    {isBoardEnded(board) && (
                      <LiveBadge type="ended" />
                    )}
                    {boardActive && (
                      <LiveBadge type="live" />
                    )}
                  </div>
                  {topic.content && (
                    <p className="text-sm font-light text-black mt-1">{topic.content}</p>
                  )}
                </div>
                <span
                  className="material-symbols-outlined text-[--color-buddha-sand] group-hover:text-[--color-primary] transition-colors duration-200 shrink-0 mt-1"
                  style={{ fontSize: '20px' }}
                >
                  chevron_right
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
